import {Alert, Button, Divider, Group, Modal, Stack} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {AntibodyEnforcedSchemaSpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {enforcedFieldSpecToField} from 'antibodies/schema-configuration/AntibodyEnforcedSchemaSpecUtils';
import {AntibodyComponentSchemaInterfaceId} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import ConstraintMigrationPreview from 'antibodies/schema-configuration/schema-migration/ConstraintMigrationPreview';
import hasEnforcedDataModelChanges from 'antibodies/schema-configuration/schema-migration/hasEnforcedDataModelChanges';
import NamingStrategyMigrationPreview from 'antibodies/schema-configuration/schema-migration/NamingStrategyMigrationPreview';
import NewFieldsMigrationPreview from 'antibodies/schema-configuration/schema-migration/NewFieldsMigrationPreview';
import {
  getRegistryEnabledNamingStrategies,
  getSelectableNamingStrategies,
  getShouldAddNamingStrategies,
} from 'antibodies/schema-configuration/schema-migration/schemaMigrationNamingStrategies';
import {
  ClientTagSchemaConstraint,
  RegistryLabelingStrategy,
  TagSchemaConstraint,
} from 'constants/RegistryConstants';
import {NamingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';

export interface AntibodyComponentMigrationPreviewModalProps {
  componentTypeName: string;
  componentInterfaceId: AntibodyComponentSchemaInterfaceId;
  registryId: string;
  enforcedSchemaSpec: AntibodyEnforcedSchemaSpec;
  existingConstraint: ClientTagSchemaConstraint | TagSchemaConstraint | null;
  existingNamingStrategies: Array<RegistryLabelingStrategy> | null;
  hasNameTemplate: boolean;
  folderItemType: string | null;
  onClose: () => void;
  onApply: (options?: {namingStrategiesToAdd: Array<NamingStrategy>}) => void | Promise<void>;
  isApplying?: boolean;
}

/**
 * Confirmation modal shown before applying a protein component type to an existing schema.
 * Previews the enforced changes the migration will make and lets the user confirm or cancel.
 */
function useNamingStrategiesToAdd(
  enforcedNamingStrategySpec: AntibodyEnforcedSchemaSpec['enforcedNamingStrategySpec'],
  existingNamingStrategies: Array<RegistryLabelingStrategy> | null,
  hasNameTemplate: boolean
): {
  shouldAddNamingStrategies: boolean;
  registryEnabledNamingStrategies: Array<NamingStrategy>;
  selectableNamingStrategies: Array<NamingStrategy>;
  selectedNamingStrategies: Array<NamingStrategy>;
  setSelectedNamingStrategies: (strategies: Array<NamingStrategy>) => void;
} {
  const shouldAddNamingStrategies = useMemo(
    () =>
      enforcedNamingStrategySpec != null &&
      getShouldAddNamingStrategies(enforcedNamingStrategySpec, existingNamingStrategies),
    [enforcedNamingStrategySpec, existingNamingStrategies]
  );
  // All deploy-enabled strategies in the spec — rendered as checkboxes (some may be disabled).
  const registryEnabledNamingStrategies = useMemo(
    () =>
      enforcedNamingStrategySpec != null
        ? getRegistryEnabledNamingStrategies(enforcedNamingStrategySpec)
        : [],
    [enforcedNamingStrategySpec]
  );
  // Subset compatible with this schema's name-template state — pre-checked and togglable.
  const selectableNamingStrategies = useMemo(
    () =>
      enforcedNamingStrategySpec != null
        ? getSelectableNamingStrategies(enforcedNamingStrategySpec, hasNameTemplate)
        : [],
    [enforcedNamingStrategySpec, hasNameTemplate]
  );
  const [selectedNamingStrategies, setSelectedNamingStrategies] =
    useState<Array<NamingStrategy>>(selectableNamingStrategies);
  useEffect(() => {
    setSelectedNamingStrategies(selectableNamingStrategies);
  }, [selectableNamingStrategies]);

  return {
    shouldAddNamingStrategies,
    registryEnabledNamingStrategies,
    selectableNamingStrategies,
    selectedNamingStrategies,
    setSelectedNamingStrategies,
  };
}

export default function AntibodyComponentMigrationPreviewModal({
  componentTypeName,
  componentInterfaceId,
  registryId,
  enforcedSchemaSpec,
  existingConstraint,
  existingNamingStrategies,
  hasNameTemplate,
  folderItemType,
  onClose,
  onApply,
  isApplying = false,
}: AntibodyComponentMigrationPreviewModalProps): JSX.Element {
  const {enforcedFieldSpecs, enforcedConstraintSpec, enforcedNamingStrategySpec} = enforcedSchemaSpec;
  const showsEnforcedChanges = hasEnforcedDataModelChanges(enforcedSchemaSpec);
  const enforcedFieldsToCreate = enforcedFieldSpecs.map((enforcedFieldSpec) =>
    enforcedFieldSpecToField(componentInterfaceId, enforcedFieldSpec, registryId)
  );
  const {
    shouldAddNamingStrategies,
    registryEnabledNamingStrategies,
    selectableNamingStrategies,
    selectedNamingStrategies,
    setSelectedNamingStrategies,
  } = useNamingStrategiesToAdd(enforcedNamingStrategySpec, existingNamingStrategies, hasNameTemplate);

  const handleApplyClick = useCallback((): void => {
    onApply(shouldAddNamingStrategies ? {namingStrategiesToAdd: selectedNamingStrategies} : undefined);
  }, [onApply, selectedNamingStrategies, shouldAddNamingStrategies]);

  const hasNoSelectableStrategies = shouldAddNamingStrategies && selectableNamingStrategies.length === 0;
  const hasNoneSelected = shouldAddNamingStrategies && selectedNamingStrategies.length === 0;
  const isApplyDisabled = isApplying || hasNoSelectableStrategies || hasNoneSelected;

  return (
    <Modal
      title={`Apply ${componentTypeName} protein component type`}
      opened={true}
      onClose={onClose}
      size="lg"
      closeOnClickOutside={!isApplying}
      closeOnEscape={!isApplying}
      withCloseButton={!isApplying}
    >
      <Stack spacing="lg">
        <Alert>
          {showsEnforcedChanges ? (
            <>
              Applying the {componentTypeName} protein component type makes the following changes to this
              schema. This action cannot be undone.
            </>
          ) : (
            <>
              Assigns the {componentTypeName} protein component type. No other schema changes. This action
              cannot be undone.
            </>
          )}
        </Alert>

        {showsEnforcedChanges && (
          <>
            <NewFieldsMigrationPreview
              fieldsToCreate={enforcedFieldsToCreate}
              enforcedFieldSpecs={enforcedFieldSpecs}
            />

            {enforcedConstraintSpec != null && (
              <ConstraintMigrationPreview
                existingConstraint={existingConstraint}
                enforcedConstraintSpec={enforcedConstraintSpec}
                enforcedFields={enforcedFieldsToCreate}
                folderItemType={folderItemType}
              />
            )}

            {enforcedNamingStrategySpec != null && (
              <NamingStrategyMigrationPreview
                enforcedNamingStrategySpec={enforcedNamingStrategySpec}
                existingNamingStrategies={existingNamingStrategies}
                registryEnabledNamingStrategies={registryEnabledNamingStrategies}
                selectableNamingStrategies={selectableNamingStrategies}
                selectedNamingStrategies={selectedNamingStrategies}
                onSelectedNamingStrategiesChange={setSelectedNamingStrategies}
              />
            )}
          </>
        )}

        {hasNoSelectableStrategies && (
          <Alert variant="error">
            None of the required labeling strategies are available for this schema. Add a name template or
            enable the required strategies on this deployment.
          </Alert>
        )}

        <Divider />

        <Group position="right">
          <Button variant="secondary" onClick={onClose} disabled={isApplying}>
            Cancel
          </Button>
          <Button onClick={handleApplyClick} loading={isApplying} disabled={isApplyDisabled}>
            Apply
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
